import { orderBy } from 'lodash';

import {
  GET_PIPELINE_LABELS,
  GET_PIPELINES,
  SET_PIPELINE_LABELS,
  SET_PIPELINES, SET_SELECTED_PIPELINE,
} from './actionTypes';

import firebase, { collections } from '../firebase';

import { nowTimestampUTC } from '../utils/date';

import { LOST_LEADS_PIPELINE_ID, ACTIVE_SURGEONS_PIPELINE_ID, pipelineTypes } from '../constants/sales';

// Pipelines

export const setPipelines = pipelines => ({ type: SET_PIPELINES, pipelines });

export const getPipelines = () => async (dispatch, getState) => {
  const state = getState();
  const tenantId = state?.tenant?.currentTenant?.id;
  const path = collections.PIPELINES_COLLECTION(tenantId);

  dispatch({ type: GET_PIPELINES });

  const snapshot = await firebase.db.collection(path).get();
  const pipelines = snapshot.docs?.map((doc) => {
    const data = doc.data();
    return {
      ...data,
      id: doc.id,
      createdAt: data.createdAt ? data.createdAt.toDate() : null,
      updatedAt: data.updatedAt ? data.updatedAt.toDate() : null,
    };
  });

  dispatch(setPipelines(orderBy(pipelines, 'title', 'asc')));

  return orderBy(pipelines, 'title', 'asc');
};

export const getPipelineById = (pipelineId) => async (dispatch, getState) => {
  const state = getState();
  const tenantId = state?.tenant?.currentTenant?.id;
  const path = collections.PIPELINES_COLLECTION(tenantId);

  dispatch({ type: GET_PIPELINES });

  const doc = await firebase.db.collection(path).doc(pipelineId).get();

  if (!doc.exists) {
    return null;
  }

  const data = doc.data();
  return {
    ...data,
    id: doc.id,
    createdAt: data.createdAt ? data.createdAt.toDate() : null,
    updatedAt: data.updatedAt ? data.updatedAt.toDate() : null,
  };
};

export const createPipeline = (pipelineData) => async (dispatch, getState) => {
  const state = getState();
  const tenantId = state.tenant.currentTenant ? state.tenant.currentTenant.id : null;
  const path = collections.PIPELINES_COLLECTION(tenantId);

  const doc = {
    ...pipelineData,
    active: true,
  }

  if (tenantId) {
    const snap = await firebase.db.collection(path).add(doc);
    return {
      id: snap.id,
      active: true,
      type: pipelineTypes.LEAD.value,
      createdAt: nowTimestampUTC(),
      updatedAt: nowTimestampUTC(),
      ...pipelineData,
    };
  } else {
    throw new Error('Invalid tenant');
  }
};

export const createLostLeadsPipeline = () => async (dispatch, getState) => {
  const state = getState();
  const tenantId = state.tenant.currentTenant ? state.tenant.currentTenant.id : null;
  const path = collections.PIPELINES_COLLECTION(tenantId);

  const doc = {
    createdAt: nowTimestampUTC(),
    active: true,
    title: 'Lost Leads',
    type: pipelineTypes.LOST.value,
  }

  if (tenantId) {
    await firebase.db.collection(path).doc(LOST_LEADS_PIPELINE_ID).set(doc);
  } else {
    throw new Error('Invalid tenant');
  }
};

export const createActiveSurgeonsPipeline = () => async (dispatch, getState) => {
  const state = getState();
  const tenantId = state.tenant.currentTenant ? state.tenant.currentTenant.id : null;
  const path = collections.PIPELINES_COLLECTION(tenantId);

  const doc = {
    createdAt: nowTimestampUTC(),
    active: true,
    title: 'Active Surgeons',
    type: pipelineTypes.ACTIVE.value,
  }

  if (tenantId) {
    await firebase.db.collection(path).doc(ACTIVE_SURGEONS_PIPELINE_ID).set(doc);
  } else {
    throw new Error('Invalid tenant');
  }
};

export const updatePipeline = (pipelineId, pipelineData) => async (dispatch, getState) => {
  const state = getState();
  const tenantId = state.tenant.currentTenant ? state.tenant.currentTenant.id : null;
  const userId = state.user.currentUser.uid;
  const path = collections.PIPELINES_COLLECTION(tenantId);

  if (tenantId) {
    await firebase.db.collection(path).doc(pipelineId).set(
      { ...pipelineData, updatedAt: nowTimestampUTC(), updatedBy: userId },
      { merge: true }
    );
  } else {
    throw new Error('Invalid tenant');
  }
};

export const deletePipeline = (pipelineId, stages) => async (dispatch, getState) => {
  const state = getState();
  const tenantId = state.tenant.currentTenant ? state.tenant.currentTenant.id : null;
  const path = collections.PIPELINES_COLLECTION(tenantId);

  if (tenantId) {
    const stagesPath = collections.PIPELINE_STAGES_COLLECTION(tenantId, pipelineId);
    const batch = firebase.db.batch();
    stages.forEach((stageId) => {
      const ref = firebase.db.collection(stagesPath).doc(stageId);
      batch.delete(ref);
    });

    await batch.commit();
    await firebase.db.collection(path).doc(pipelineId).delete();
  } else {
    throw new Error('Invalid tenant');
  }
};

export const setSelectedPipeline = (pipelineId) => ({ type: SET_SELECTED_PIPELINE, pipeline: pipelineId });

// Labels

export const setPipelineLabels = labels => ({ type: SET_PIPELINE_LABELS, labels });

export const getPipelineLabels = () => async (dispatch, getState) => {
  const state = getState();
  const tenantId = state?.tenant?.currentTenant?.id;
  const path = collections.PIPELINE_LABELS_COLLECTION(tenantId);

  dispatch({ type: GET_PIPELINE_LABELS });

  const snapshot = await firebase.db.collection(path).get();
  const labels = snapshot.docs?.map(doc => ({
    id: doc.id,
    ...doc.data(),
  }));

  dispatch(setPipelineLabels(orderBy(labels, 'name', 'asc')));

  return orderBy(labels, 'name', 'asc');
};

export const createPipelineLabel = (labelData) => async (dispatch, getState) => {
  const state = getState();
  const tenantId = state.tenant.currentTenant ? state.tenant.currentTenant.id : null;
  const path = collections.PIPELINE_LABELS_COLLECTION(tenantId);

  const doc = {
    ...labelData,
    active: true,
  }

  if (tenantId) {
    const snap = await firebase.db.collection(path).add(doc);
    return {
      id: snap.id,
      active: true,
      ...labelData,
    };
  } else {
    throw new Error('Invalid tenant');
  }
};

export const updatePipelineLabel = (labelId, labelData) => async (dispatch, getState) => {
  const state = getState();
  const tenantId = state.tenant.currentTenant ? state.tenant.currentTenant.id : null;
  const path = collections.PIPELINE_LABELS_COLLECTION(tenantId);

  if (tenantId) {
    await firebase.db.collection(path).doc(labelId).set(labelData, { merge: true });
  } else {
    throw new Error('Invalid tenant');
  }
};

export const deletePipelineLabel = (labelId) => async (dispatch, getState) => {
  const state = getState();
  const tenantId = state.tenant.currentTenant ? state.tenant.currentTenant.id : null;
  const path = collections.PIPELINE_LABELS_COLLECTION(tenantId);

  if (tenantId) {
    await firebase.db.collection(path).doc(labelId).delete();
  } else {
    throw new Error('Invalid tenant');
  }
};

// Stages

export const getPipelineStages = (pipelineId) => async (dispatch, getState) => {
  const state = getState();
  const tenantId = state.tenant.currentTenant ? state.tenant.currentTenant.id : null;
  const path = collections.PIPELINE_STAGES_COLLECTION(tenantId, pipelineId);

  if (tenantId) {
    const snapshot = await firebase.db.collection(path).get();
    const docs = snapshot.docs?.map((doc) => {
      const data = doc.data();
      return {
        ...data,
        id: doc.id,
        createdAt: data.createdAt ? data.createdAt.toDate() : null,
        updatedAt: data.updatedAt ? data.updatedAt.toDate() : null,
        pipelineId,
      };
    });

    return orderBy(docs, 'orderNumber', 'asc');
  } else {
    throw new Error('Invalid tenant');
  }
};

export const createPipelineStage = (pipelineId, data) => async (dispatch, getState) => {
  const state = getState();
  const tenantId = state.tenant.currentTenant ? state.tenant.currentTenant.id : null;
  const path = collections.PIPELINE_STAGES_COLLECTION(tenantId, pipelineId);

  const doc = {
    ...data,
    active: true,
    createdAt: nowTimestampUTC(),
    updatedAt: nowTimestampUTC(),
  }

  if (tenantId) {
    const snap = await firebase.db.collection(path).add(doc);
    return {
      ...doc,
      id: snap.id,
    };
  } else {
    throw new Error('Invalid tenant');
  }
};

export const updatePipelineStage = (pipelineId, stageId, data) => async (dispatch, getState) => {
  const state = getState();
  const tenantId = state.tenant.currentTenant ? state.tenant.currentTenant.id : null;
  const userId = state.user.currentUser.uid;
  const path = collections.PIPELINE_STAGES_COLLECTION(tenantId, pipelineId);

  if (tenantId) {
    await firebase.db.collection(path).doc(stageId).set(
      { ...data, updatedAt: nowTimestampUTC(), updatedBy: userId },
      { merge: true }
    );
  } else {
    throw new Error('Invalid tenant');
  }
};

export const deletePipelineStage = (pipelineId, stageId) => async (dispatch, getState) => {
  const state = getState();
  const tenantId = state.tenant.currentTenant ? state.tenant.currentTenant.id : null;
  const path = collections.PIPELINE_STAGES_COLLECTION(tenantId, pipelineId);

  if (tenantId) {
    await firebase.db.collection(path).doc(stageId).delete();
  } else {
    throw new Error('Invalid tenant');
  }
};

// Subscriptions

export const subscribeToPipelines = () => (dispatch, getState) => {
  const state = getState();
  const tenantId = state.tenant.currentTenant ? state.tenant.currentTenant.id : null;
  const path = collections.PIPELINES_COLLECTION(tenantId);

  return firebase
    .db
    .collection(path)
    .onSnapshot({
      error: (e) => console.error(e),
      next: (querySnapshot) => {
        let pipelines = [];
        querySnapshot.forEach((documentSnapshot) => {
          const id = documentSnapshot.id;
          const data = documentSnapshot.data();
          pipelines = [...pipelines, {
            ...data,
            id,
            createdAt: data.createdAt ? data.createdAt.toDate() : null,
            updatedAt: data.updatedAt ? data.updatedAt.toDate() : null,
          }];
        });

        return dispatch(setPipelines(orderBy(pipelines, 'title', 'asc')));
      }
    });
};

export const subscribeToPipelineLabels = () => (dispatch, getState) => {
  const state = getState();
  const tenantId = state.tenant.currentTenant ? state.tenant.currentTenant.id : null;
  const path = collections.PIPELINE_LABELS_COLLECTION(tenantId);

  return firebase
    .db
    .collection(path)
    .onSnapshot({
      error: (e) => console.error(e),
      next: (querySnapshot) => {
        let labels = [];
        querySnapshot.forEach((documentSnapshot) => {
          const id = documentSnapshot.id;
          const data = documentSnapshot.data();
          labels = [...labels, {
            ...data,
            id,
          }];
        });

        return dispatch(setPipelineLabels(orderBy(labels, 'name', 'asc')));
      }
    });
};
